import type { Node } from "@mapcraft-tasks/client-store";
import {
	useAreas,
	useConflicts,
	useInbox,
	useOverdue,
	usePinned,
	useSyncStatus
} from "@mapcraft-tasks/client-store/react";
import { Link, type LinkProps, useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { DateBadge } from "./date-badge";

/**
 * Сайдбар — ШОРТКАТЫ, а не дерево. Дерево вглубь здесь не разворачивается: это
 * дублировало бы основную панель и создавало два места, где кликается одно и то же.
 * Навигация вглубь — только zoom-in (ui-spec Область 1).
 */
const LINKS = [
	{ to: "/today", label: "Сегодня", chord: "gt" },
	{ to: "/inbox", label: "Входящие", chord: "gi" },
	{ to: "/projects", label: "Проекты", chord: "gp" },
	{ to: "/calendar", label: "Календарь", chord: "gc" },
	{ to: "/recurring", label: "Повторяющиеся", chord: "gr" },
	{ to: "/overdue", label: "Просрочено", chord: "gd" },
	{ to: "/archive", label: "Архив", chord: "ga" }
] as const;

export function Sidebar({ drawer, onNavigate }: { drawer?: boolean; onNavigate?: () => void } = {}) {
	const inbox = useInbox();
	const overdue = useOverdue();
	const areas = useAreas();
	const pins = usePinned();
	const conflicts = useConflicts();
	const status = useSyncStatus();
	const path = useRouterState({ select: (s) => s.location.pathname });

	return (
		/*
		 * Фон у сайдбара прозрачный — он лежит прямо на подложке приложения, как у Tana.
		 * Рамки справа нет: панель рядом и так отделена собственным фоном (в тёмной)
		 * или тенью (в светлой).
		 *
		 * В выдвижной панели телефона высоту не занимаем: там сайдбар — верхняя секция
		 * общего скроллера, а не отдельная колонка (см. mobile/chrome).
		 */
		<nav className={drawer ? "flex flex-col" : "flex h-full w-[var(--sidebar-width)] shrink-0 flex-col"}>
			{/* В шторке заголовок «Tasks» не нужен — приложение и так на экране. */}
			{!drawer && (
				<div className="px-3 pt-3 pb-4">
					<Link to="/today" className="text-sm font-bold text-ink-strong">
						Tasks
					</Link>
				</div>
			)}

			<ul className={drawer ? "px-2 pt-3" : "px-2"}>
				{LINKS.map((link) => (
					<li key={link.to}>
						<Link
							to={link.to}
							onClick={onNavigate}
							className={`group nav-item flex items-baseline justify-between gap-2 rounded-sm px-2 py-[3px] text-sm transition-colors ${
								path === link.to
									? "bg-active text-ink-strong"
									: "text-ink hover:bg-active hover:text-ink-strong"
							}`}
						>
							<span className="flex items-baseline gap-2 truncate">
								<span className="truncate">{link.label}</span>
								{link.to === "/inbox" && inbox.length > 0 && (
									<span className="tabular shrink-0 text-xs font-light text-ink-muted">
										{inbox.length}
									</span>
								)}
								{link.to === "/overdue" && overdue.length > 0 && (
									<span className="tabular shrink-0 text-xs font-light text-danger">
										{overdue.length}
									</span>
								)}
							</span>
							<span className="kbd shrink-0 opacity-0 transition-opacity group-hover:opacity-100">
								{link.chord}
							</span>
						</Link>
					</li>
				))}
			</ul>

			{/* Появляется, только когда карантин непуст: ненулевой счётчик по умолчанию
			    приучает его не читать (offline-sync §10.3). */}
			{conflicts.length > 0 && (
				<Link
					to="/conflicts"
					onClick={onNavigate}
					className="mx-2 mt-2 flex items-baseline justify-between gap-2 rounded-sm border border-danger/40 bg-danger-soft px-2 py-1 text-sm text-danger transition-colors hover:border-danger/70"
				>
					<span>⚠ Конфликты</span>
					<span className="tabular">{conflicts.length}</span>
				</Link>
			)}

			{/* Пины — отдельная секция, а не пункт меню: важно видеть сами закреплённые
			    ноды, а не ссылку на страницу со списком. Заголовок остаётся ссылкой
			    на /pinned, чтобы страница не потерялась. */}
			{pins.length > 0 && (
				<Section title="Пины" to="/pinned" drawer={drawer} onNavigate={onNavigate}>
					{pins.map((entry) => (
						<NodeLink key={entry.node.id} node={entry.node} onNavigate={onNavigate} />
					))}
				</Section>
			)}

			{areas.length > 0 && (
				<Section title="Области" drawer={drawer} onNavigate={onNavigate}>
					{areas.map((area) => (
						<NodeLink key={area.id} node={area} onNavigate={onNavigate} />
					))}
				</Section>
			)}

			<div className={`px-3 py-3 ${drawer ? "" : "mt-auto"}`}>
				<SyncBadge {...status} />
			</div>
		</nav>
	);
}

/** Секция сайдбара: подпись (иногда — ссылка на свою страницу) и плоский список нод. */
function Section({
	title,
	to,
	drawer,
	onNavigate,
	children
}: {
	title: string;
	to?: LinkProps["to"];
	drawer?: boolean;
	onNavigate?: () => void;
	children: ReactNode;
}) {
	return (
		<>
			<div className="section-label mt-6 px-3 pb-1">
				{to ? (
					<Link to={to} onClick={onNavigate} className="transition-colors hover:text-ink">
						{title}
					</Link>
				) : (
					title
				)}
			</div>
			<ul className={drawer ? "px-2" : "overflow-y-auto px-2"}>{children}</ul>
		</>
	);
}

function NodeLink({ node, onNavigate }: { node: Node; onNavigate?: () => void }) {
	return (
		<li>
			<Link
				to="/n/$nodeId"
				params={{ nodeId: node.id }}
				onClick={onNavigate}
				className="nav-item flex items-baseline gap-2 rounded-sm px-2 py-[3px] text-sm text-ink transition-colors hover:bg-active hover:text-ink-strong"
			>
				<span className="min-w-0 flex-1 truncate">{node.title}</span>
				<DateBadge node={node} />
			</Link>
		</li>
	);
}

/**
 * ЕДИНСТВЕННЫЙ индикатор сети во всём интерфейсе. Не на кнопках, не на строках:
 * `dispatch` синхронен, и «сохраняется…» показывать нечему (ui-spec Область 1).
 */
function SyncBadge({
	online,
	pending,
	loading,
	lastSyncedAt,
	error
}: {
	online: boolean;
	pending: number;
	loading: boolean;
	lastSyncedAt: Date | null;
	error: string | null;
}) {
	const dot = loading ? "bg-ink-faint" : error || !online ? "bg-danger" : pending > 0 ? "bg-link" : "bg-positive";
	const label = loading
		? "загрузка"
		: !online
			? "нет связи"
			: pending > 0
				? `${pending} в очереди`
				: lastSyncedAt
					? `синхронизировано ${lastSyncedAt.toLocaleTimeString("ru", { hour: "2-digit", minute: "2-digit" })}`
					: "готово";

	return (
		<div className="flex items-center gap-2 text-xs font-light text-ink-muted" title={error ?? undefined}>
			<span className={`size-[6px] shrink-0 rounded-full ${dot}`} />
			{label}
		</div>
	);
}
